/** Classes admin top-level page (CS-01..CS-06). Weekly calendar with a PT-only
 *  filter, a trainer "My Sessions" tab, and the session edit/cancel modal. */
import type { types } from '@gym/shared';
import { Filter } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, PageHeader, SegmentedControl } from '../../components/ui.js';
import { useAuth } from '../../lib/auth.js';

import { ClassCalendar } from './ClassCalendar.js';
import { ClassSessionEditModal } from './ClassSessionEditModal.js';

type Tab = 'calendar' | 'my_sessions';

export function ClassesPage(): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const isTrainer = claims?.app_role === 'trainer';
  const [tab, setTab] = useState<Tab>('calendar');
  const [onlyPt, setOnlyPt] = useState(false);
  const [selected, setSelected] = useState<types.ClassSessionDTO | null>(null);

  return (
    <div className="grid gap-5">
      <PageHeader
        title={t('classes.calendar.title')}
        subtitle={t('classes.calendar.week_view')}
        actions={
          <>
            {isTrainer ? (
              <SegmentedControl<Tab>
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'calendar', label: t('classes.calendar.week_view') },
                  { value: 'my_sessions', label: t('classes.calendar.my_sessions_tab') },
                ]}
              />
            ) : null}
            <Button
              variant={onlyPt ? 'brand' : 'secondary'}
              aria-pressed={onlyPt}
              icon={<Filter className="h-4 w-4" />}
              onClick={() => setOnlyPt((v) => !v)}
            >
              {t('classes.calendar.pt_filter')}
            </Button>
          </>
        }
      />

      {tab === 'my_sessions' && isTrainer && claims?.staff_id ? (
        <ClassCalendar
          instructorStaffId={claims.staff_id}
          onlyPt={onlyPt}
          onSessionClick={setSelected}
        />
      ) : (
        <ClassCalendar onlyPt={onlyPt} onSessionClick={setSelected} />
      )}

      {selected ? (
        <Card className="p-5">
          <ClassSessionEditModal session={selected} onClose={() => setSelected(null)} />
        </Card>
      ) : null}
    </div>
  );
}
